import {
  Activity,
  AlertTriangle,
  Axe,
  Ban,
  Box,
  Calendar,
  CalendarClock,
  Database,
  FileSearch,
  FileText,
  Gauge,
  Gavel,
  Hammer,
  Handshake,
  History,
  Image,
  KanbanSquare,
  LayoutDashboard,
  LineChart,
  ListFilter,
  MapPin,
  MessageSquare,
  Network,
  Package,
  PartyPopper,
  Server,
  ScrollText,
  ShieldCheck,
  ShoppingCart,
  StickyNote,
  Terminal,
  UserCog,
  NotebookPen,
  Users,
  Vote,
} from "lucide-react";
import { Identity } from "../types/auth";
import { getLimitedTesseractTeamAccess, getTeamScopedStaff, TESSERACT_TEAM_CURATORS } from "../utils/tesseractTeamAccess";
import { canViewStaffPanel, getStaffPerms } from "../utils/staffPermissions";
import { canViewModpackConfigs } from "../utils/modpackConfigPermissions";
import { canViewScheduledCommands } from "../utils/scheduledCommandPermissions";
import { canViewPartnerships } from "../utils/partnershipPermissions";
import { canAccessDataController } from "../utils/dataControllerPermissions";
import { canViewRelationships } from '../utils/relationshipPermissions';
import { canViewLogbook } from '../utils/logbookPermissions';
import { formPermissions } from '../features/forms/permissions';
import { canViewKanbanWorkspaces } from '../utils/kanbanPermissions';

export interface NavigationVisibilityContext {
  identity: Identity | null;
  isDev: boolean;
}

export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  category: string;
  isVisible: (context: NavigationVisibilityContext) => boolean;
  isActive?: (pathname: string) => boolean;
}

export interface NavigationCategory {
  label: string;
  items: NavigationItem[];
}

/**
 * General staff tooling; hidden from team-scoped staff (builders, event team, newsletter,
 * curators with no other staff team — see getTeamScopedStaff), who only get their own panels.
 */
const fullStaffNavOnly = ({ identity, isDev }: NavigationVisibilityContext) =>
  getTeamScopedStaff(identity, isDev) == null;

/** Curator panels: full staff, or team-scoped staff on the Curators team. */
const curatorOrFullStaff = ({ identity, isDev }: NavigationVisibilityContext) => {
  const scoped = getTeamScopedStaff(identity, isDev);
  return scoped == null || scoped.teams.includes(TESSERACT_TEAM_CURATORS);
};

const hasGroupContaining = (
  identity: Identity | null,
  groupFragment: string
) => {
  const groups = identity?.raw?.custom?.groups || [];
  return groups.some((group: string) => group.includes(groupFragment));
};

const hasAnyGroup = (identity: Identity | null, targetGroups: string[]) => {
  const groups = identity?.raw?.custom?.groups || [];
  return targetGroups.some((targetGroup) => groups.includes(targetGroup));
};

const hasPermission = (identity: Identity | null, permission: string) => {
  const permissions = identity?.raw?.custom?.permissions || [];
  return permissions.includes(permission);
};

export const NAVIGATION_ITEMS: NavigationItem[] = [
  { id: 'forms', label: 'Forms', path: '/forms', icon: NotebookPen, category: 'Community', isVisible: ({identity,isDev}) => formPermissions(identity,isDev).read, isActive: path => path === '/forms' || path.startsWith('/forms/') },
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/dashboard",
    icon: LayoutDashboard,
    category: "Overview",
    // Every signed-in staff member, team-scoped staff included.
    isVisible: ({ identity, isDev }) => isDev || identity != null,
  },
  {
    id: "player-data",
    label: "Player Data",
    path: "/player",
    icon: Users,
    category: "Players",
    isVisible: fullStaffNavOnly,
    isActive: (pathname) =>
      pathname.startsWith("/player") &&
      !pathname.startsWith("/player-stream-log") &&
      !pathname.startsWith("/player-spawns"),
  },
  {
    id: 'relationships',
    label: 'Relationships',
    path: '/relationships',
    icon: Network,
    category: 'Players',
    isVisible: ({ identity }) => canViewRelationships(identity),
  },
  {
    id: "player-stream-log",
    label: "Player Stream Log",
    path: "/player-stream-log",
    icon: Activity,
    category: "Players",
    isVisible: fullStaffNavOnly,
  },
  {
    id: "votes",
    label: "Votes",
    path: "/votes",
    icon: Vote,
    category: "Players",
    isVisible: fullStaffNavOnly,
  },
  {
    id: "notes-warnings",
    label: "Notes & Warnings",
    path: "/notes-warnings",
    icon: FileText,
    category: "Moderation",
    isVisible: ({ identity, isDev }) =>
      isDev || hasGroupContaining(identity, "Notes"),
  },
  {
    id: "bans",
    label: "Bans",
    path: "/bans",
    icon: Axe,
    category: "Moderation",
    isVisible: ({ identity, isDev }) =>
      isDev || hasGroupContaining(identity, "Bans"),
  },
  {
    id: "logbook",
    label: "Logbook",
    path: "/logbook",
    icon: ScrollText,
    category: "Moderation",
    // No development bypass: evidence reads are audited, so the permission node decides.
    isVisible: ({ identity }) => canViewLogbook(identity),
    isActive: (pathname) => pathname.startsWith("/logbook"),
  },
  {
    id: "evidence",
    label: "Evidence",
    path: "/evidence",
    icon: FileSearch,
    category: "Moderation",
    isVisible: ({ identity, isDev }) =>
      isDev || hasGroupContaining(identity, "Notes"),
  },
  {
    id: "appeals",
    label: "Ban Appeals",
    path: "/appeals",
    icon: Gavel,
    category: "Moderation",
    isVisible: ({ identity, isDev }) =>
      isDev ||
      hasAnyGroup(identity, [
        "SPY_Bans_Helper",
        "SPY_Bans_Mod",
        "SPY_Bans_Admin",
        "SPY_Bans_Superadmins",
        "SPY_Super_admin",
      ]),
    isActive: (pathname) => pathname.startsWith("/appeals"),
  },
  {
    id: "incidents",
    label: "Incidents",
    path: "/incidents",
    icon: AlertTriangle,
    category: "Moderation",
    isVisible: ({ identity, isDev }) =>
      isDev ||
      hasAnyGroup(identity, ["SPY_Notes", "SPY_Notes_Editor"]),
  },
  {
    id: "chat-logs",
    label: "Chat Logs",
    path: "/chat",
    icon: MessageSquare,
    category: "Audit Logs",
    isVisible: fullStaffNavOnly,
  },
  {
    id: "command-viewer",
    label: "Command Viewer",
    path: "/command-log",
    icon: Terminal,
    category: "Audit Logs",
    isVisible: fullStaffNavOnly,
  },
  {
    id: "item-spawn-log",
    label: "Item Spawn Log",
    path: "/player-spawns",
    icon: Box,
    category: "Audit Logs",
    isVisible: fullStaffNavOnly,
  },
  {
    id: "shop-changelog",
    label: "Shop Changelog",
    path: "/shop-changelog",
    icon: History,
    category: "Curators",
    isVisible: curatorOrFullStaff,
  },
  {
    id: "economy",
    label: "Economy",
    path: "/economy",
    icon: ShoppingCart,
    category: "Economy & Game",
    isVisible: curatorOrFullStaff,
  },
  {
    id: "warps",
    label: "Warps",
    path: "/warps",
    icon: MapPin,
    category: "Economy & Game",
    isVisible: fullStaffNavOnly,
  },
  {
    id: "item-bans",
    label: "Item Bans",
    path: "/item-bans",
    icon: Ban,
    category: "Network Config",
    isVisible: fullStaffNavOnly,
  },
  {
    id: "spawn-limits",
    label: "Spawn Limits",
    path: "/spawn-limits",
    icon: ListFilter,
    category: "Network Config",
    isVisible: fullStaffNavOnly,
  },
  {
    id: "scheduled-commands",
    label: "Scheduled Commands",
    path: "/scheduled-commands",
    icon: CalendarClock,
    category: "Network Config",
    // Shares its check with the route guard in App.tsx so the sidebar can
    // never offer a page the router will not render.
    isVisible: ({ identity, isDev }) => isDev || canViewScheduledCommands(identity),
    isActive: (pathname) => pathname.startsWith("/scheduled-commands"),
  },
  {
    id: "status-monitor",
    label: "Status Monitor",
    path: "/status-monitor",
    icon: Server,
    category: "Infrastructure",
    isVisible: fullStaffNavOnly,
  },
  {
    id: "starchart",
    label: "StarChart",
    path: "/starchart",
    icon: LineChart,
    category: "Infrastructure",
    isVisible: fullStaffNavOnly,
    isActive: (pathname) => pathname.startsWith("/starchart"),
  },
  {
    id: "atlas",
    label: "Atlas",
    path: "/atlas",
    icon: Image,
    category: "Infrastructure",
    isVisible: ({ identity, isDev }) =>
      isDev || hasPermission(identity, "argus.atlas.read"),
  },
  {
    id: "chunk-throttle",
    label: "Chunk Throttle",
    path: "/chunk-throttle",
    icon: Gauge,
    category: "Infrastructure",
    isVisible: ({ identity, isDev }) =>
      isDev ||
      hasAnyGroup(identity, [
        "SPY_Bans_Mod",
        "SPY_Bans_Admin",
        "SPY_Bans_Superadmins",
        "SPY_Super_admin",
      ]),
    isActive: (pathname) => pathname.startsWith("/chunk-throttle"),
  },
  {
    id: "update-advisor",
    label: "Update Advisor",
    path: "/update-advisor",
    icon: Package,
    category: "Infrastructure",
    isVisible: fullStaffNavOnly,
  },
  {
    id: "modpack-notes",
    label: "Modpack Notes",
    path: "/modpack-notes",
    icon: StickyNote,
    category: "Infrastructure",
    isVisible: ({ identity, isDev }) =>
      isDev ||
      hasAnyGroup(identity, ["argus_modpack_read", "SPY_Super_admin"]),
  },
  {
    id: "modpack-configs",
    label: "Modpack Configs",
    path: "/modpack-configs",
    icon: Server,
    category: "Infrastructure",
    // Shares its check with the route guard in App.tsx so the sidebar can
    // never offer a page the router will not render.
    isVisible: ({ identity, isDev }) => isDev || canViewModpackConfigs(identity),
    isActive: (pathname) => pathname.startsWith("/modpack-configs"),
  },
  {
    id: "build-team",
    label: "Build Team",
    path: "/build-team",
    icon: Hammer,
    category: "Build Team",
    isVisible: ({ identity, isDev }) => {
      const limited = getLimitedTesseractTeamAccess(identity, isDev);
      if (limited && !limited.calendarViewOnly && limited.role === "buildteam") return true;
      return (
        isDev ||
        hasAnyGroup(identity, [
          "argus_build_panel",
          "argus_build_lead",
          "argus_build_admin",
          "SPY_Super_admin",
        ])
      );
    },
    isActive: (pathname) => pathname.startsWith("/build-team"),
  },
  {
    id: "event-team",
    label: "Event Team",
    path: "/event-team",
    icon: PartyPopper,
    category: "Event Team",
    isVisible: ({ identity, isDev }) => {
      const limited = getLimitedTesseractTeamAccess(identity, isDev);
      if (limited && !limited.calendarViewOnly && limited.role === "eventteam") return true;
      return (
        isDev ||
        hasAnyGroup(identity, [
          "argus_event_panel",
          "argus_event_lead",
          "argus_event_admin",
          "SPY_Super_admin",
        ])
      );
    },
    isActive: (pathname) => pathname.startsWith("/event-team"),
  },
  {
    id: "partnerships",
    label: "Partnerships",
    path: "/partnerships",
    icon: Handshake,
    category: "PR Management",
    isVisible: ({ identity, isDev }) => isDev || canViewPartnerships(identity),
    isActive: (pathname) => pathname.startsWith("/partnerships"),
  },
  {
    id: "modpack-browser",
    label: "Modpack Browser",
    path: "/modpacks",
    icon: Database,
    category: "Planning",
    isVisible: fullStaffNavOnly,
    isActive: (pathname) => pathname.startsWith("/modpacks"),
  },
  {
    id: "workspaces",
    label: "Workspaces",
    path: "/workspaces",
    icon: KanbanSquare,
    category: "Planning",
    isVisible: ({ identity, isDev }) => canViewKanbanWorkspaces(identity, isDev),
    isActive: (pathname) => pathname.startsWith("/workspaces"),
  },
  {
    id: "calendar",
    label: "Calendar",
    path: "/calendar",
    icon: Calendar,
    category: "Planning",
    isVisible: ({ identity, isDev }) => {
      if (isDev || hasPermission(identity, "argus.calendar.view")) return true;
      const limited = getLimitedTesseractTeamAccess(identity, isDev);
      return limited != null && limited.role !== TESSERACT_TEAM_CURATORS;
    },
  },
  {
    id: "staff-manager",
    label: "Staff Manager",
    path: "/staff",
    icon: UserCog,
    category: "Staff Management",
    isVisible: ({ identity, isDev }) => canViewStaffPanel(identity, isDev),
    isActive: (pathname) =>
      pathname === "/staff" ||
      (pathname.startsWith("/staff/") && !pathname.startsWith("/staff/notes") && !pathname.startsWith("/staff/data-controller")),
  },
  {
    id: "staff-notes",
    label: "Staff Notes",
    path: "/staff/notes",
    icon: NotebookPen,
    category: "Staff Management",
    isVisible: ({ identity, isDev }) => getStaffPerms(identity, isDev).canReadNotes,
    isActive: (pathname) => pathname.startsWith("/staff/notes"),
  },
  {
    id: "data-controller",
    label: "Data Controller",
    path: "/staff/data-controller",
    icon: ShieldCheck,
    category: "Staff Management",
    // argus.datacontroller node, or SPY_Super_admin (usual bypass).
    isVisible: ({ identity, isDev }) => canAccessDataController(identity, isDev),
    isActive: (pathname) => pathname.startsWith("/staff/data-controller"),
  },
];

const NAVIGATION_CATEGORY_ORDER = [
  "Overview",
  "Community",
  "Players",
  "Moderation",
  "Audit Logs",
  "Economy & Game",
  "Network Config",
  "Infrastructure",
  "Build Team",
  "Event Team",
  "PR Management",
  "Curators",
  "Planning",
  "Staff Management",
];

export const isNavItemActive = (item: NavigationItem, pathname: string) => {
  if (item.isActive) {
    return item.isActive(pathname);
  }
  return pathname.startsWith(item.path);
};

export const getVisibleNavigationItems = (
  context: NavigationVisibilityContext
) => NAVIGATION_ITEMS.filter((item) => item.isVisible(context));

export const getVisibleNavigation = (
  context: NavigationVisibilityContext
): NavigationCategory[] => {
  const visibleItems = getVisibleNavigationItems(context);
  return NAVIGATION_CATEGORY_ORDER.map((label) => ({
    label,
    items: visibleItems.filter((item) => item.category === label),
  })).filter((category) => category.items.length > 0);
};

export const getPageTitleForPath = (pathname: string) => {
  const matchingItems = NAVIGATION_ITEMS.filter((item) =>
    isNavItemActive(item, pathname)
  );
  if (matchingItems.length === 0) {
    if (pathname.startsWith("/settings")) {
      return "Settings";
    }
    if (pathname.startsWith("/dev/quest-viewer")) {
      return "Quest viewer";
    }
    return "ARGUS";
  }
  const [bestMatch] = matchingItems.sort((a, b) => b.path.length - a.path.length);
  return bestMatch.label;
};
